if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');

        if (!this.form) {
          console.warn('ProductForm: form not found.');
          return;
        }

        const idInput = this.form.querySelector('[name="id"]');

        if (idInput) {
          idInput.disabled = false;
        }

        this.form.addEventListener(
          'submit',
          this.onSubmitHandler.bind(this)
        );

        this.cart =
          document.querySelector('cart-drawer') ||
          document.querySelector('cart-notification');

        this.submitButton = this.querySelector('[type="submit"]');

        if (
          this.submitButton &&
          document.querySelector('cart-drawer')
        ) {
          this.submitButton.setAttribute(
            'aria-haspopup',
            'dialog'
          );
        }

        this.hideErrors =
          this.dataset.hideErrors === 'true';
      }

      async onSubmitHandler(evt) {
        /*
         * IMPORTANT:
         * Never allow the normal Shopify form submit.
         */
        evt.preventDefault();
        evt.stopPropagation();

        if (!this.form || !this.submitButton) {
          return;
        }

        if (
          this.submitButton.getAttribute(
            'aria-disabled'
          ) === 'true'
        ) {
          return;
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute(
          'aria-disabled',
          true
        );

        this.submitButton.classList.add('loading');

        const spinner = this.querySelector(
          '.loading-overlay__spinner'
        );

        if (spinner) {
          spinner.classList.remove('hidden');
        }

        try {
          const config =
            typeof fetchConfig === 'function'
              ? fetchConfig('javascript')
              : {
                  method: 'POST',
                  headers: {
                    Accept: 'application/json',
                    'X-Requested-With':
                      'XMLHttpRequest',
                  },
                };

          config.headers =
            config.headers || {};

          config.headers['X-Requested-With'] =
            'XMLHttpRequest';

          /*
           * FormData needs browser-generated
           * multipart boundary.
           */
          delete config.headers['Content-Type'];

          const formData = new FormData(
            this.form
          );

          /*
           * Request cart drawer sections when
           * the theme supports them.
           */
          if (
            this.cart &&
            typeof this.cart.getSectionsToRender ===
              'function'
          ) {
            const sections =
              this.cart.getSectionsToRender();

            const sectionIds = sections
              .map(
                (section) =>
                  section.id ||
                  section.section
              )
              .filter(Boolean);

            if (sectionIds.length) {
              formData.append(
                'sections',
                sectionIds.join(',')
              );

              formData.append(
                'sections_url',
                window.location.pathname
              );
            }
          } else {
            /*
             * Dawn-compatible fallback.
             *
             * This prevents Shopify from needing
             * a full page navigation just because
             * the custom element was unavailable.
             */
            formData.append(
              'sections',
              'cart-drawer,cart-icon-bubble'
            );

            formData.append(
              'sections_url',
              window.location.pathname
            );
          }

          if (
            this.cart &&
            typeof this.cart.setActiveElement ===
              'function'
          ) {
            this.cart.setActiveElement(
              document.activeElement
            );
          }

          config.body = formData;

          const cartAddUrl =
            window.routes?.cart_add_url ||
            '/cart/add.js';

          const fetchResponse = await fetch(
            cartAddUrl,
            config
          );

          const response =
            await fetchResponse.json();

          /*
           * Shopify error such as sold-out
           * or unavailable quantity.
           */
          if (
            response.status ||
            !fetchResponse.ok
          ) {
            if (
              typeof publish === 'function' &&
              typeof PUB_SUB_EVENTS !==
                'undefined'
            ) {
              publish(
                PUB_SUB_EVENTS.cartError,
                {
                  source: 'product-form',
                  productVariantId:
                    formData.get('id'),
                  errors:
                    response.description,
                  message: response.message,
                }
              );
            }

            this.handleErrorMessage(
              response.description ||
                response.message ||
                'Unable to add this product.'
            );

            const soldOutMessage =
              this.submitButton.querySelector(
                '.sold-out-message'
              );

            if (soldOutMessage) {
              this.submitButton.setAttribute(
                'aria-disabled',
                true
              );

              const text =
                this.submitButton.querySelector(
                  'span'
                );

              if (text) {
                text.classList.add('hidden');
              }

              soldOutMessage.classList.remove(
                'hidden'
              );
            }

            this.error = true;

            return;
          }

          this.error = false;

          /*
           * Tell rest of theme cart changed.
           */
          if (
            typeof publish === 'function' &&
            typeof PUB_SUB_EVENTS !==
              'undefined'
          ) {
            publish(
              PUB_SUB_EVENTS.cartUpdate,
              {
                source: 'product-form',
                productVariantId:
                  formData.get('id'),
              }
            );
          }

          const quickAddModal =
            this.closest('quick-add-modal');

          /*
           * BEST PATH:
           * Use the theme's own cart drawer
           * renderer.
           */
          if (
            this.cart &&
            typeof this.cart.renderContents ===
              'function'
          ) {
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(
                      response
                    );
                  });
                },
                { once: true }
              );

              if (
                typeof quickAddModal.hide ===
                'function'
              ) {
                quickAddModal.hide(true);
              }
            } else {
              this.cart.renderContents(
                response
              );
            }

            return;
          }

          /*
           * FALLBACK:
           * Update Dawn-style drawer manually.
           */
          this.renderFallbackCart(response);
        } catch (error) {
          console.error(
            'Product add error:',
            error
          );

          this.handleErrorMessage(
            'There was a problem adding this product to your cart.'
          );
        } finally {
          this.submitButton.classList.remove(
            'loading'
          );

          if (
            this.cart &&
            this.cart.classList.contains(
              'is-empty'
            )
          ) {
            this.cart.classList.remove(
              'is-empty'
            );
          }

          if (!this.error) {
            this.submitButton.removeAttribute(
              'aria-disabled'
            );
          }

          if (spinner) {
            spinner.classList.add('hidden');
          }
        }
      }

      renderFallbackCart(response) {
        if (!response?.sections) {
          console.warn(
            'ProductForm: cart sections were not returned.'
          );
          return;
        }

        /*
         * CART DRAWER
         */
        if (response.sections['cart-drawer']) {
          const documentHTML =
            new DOMParser().parseFromString(
              response.sections[
                'cart-drawer'
              ],
              'text/html'
            );

          const newDrawer =
            documentHTML.querySelector(
              'cart-drawer'
            );

          const currentDrawer =
            document.querySelector(
              'cart-drawer'
            );

          if (newDrawer && currentDrawer) {
            currentDrawer.innerHTML =
              newDrawer.innerHTML;

            currentDrawer.classList.remove(
              'is-empty'
            );
          }
        }

        /*
         * CART COUNT BUBBLE
         */
        if (
          response.sections[
            'cart-icon-bubble'
          ]
        ) {
          const bubbleHTML =
            new DOMParser().parseFromString(
              response.sections[
                'cart-icon-bubble'
              ],
              'text/html'
            );

          const newBubble =
            bubbleHTML.querySelector(
              '#cart-icon-bubble'
            );

          const currentBubble =
            document.querySelector(
              '#cart-icon-bubble'
            );

          if (newBubble && currentBubble) {
            currentBubble.innerHTML =
              newBubble.innerHTML;
          }
        }

        /*
         * OPEN DRAWER
         */
        const drawer =
          document.querySelector(
            'cart-drawer'
          );

        if (drawer) {
          if (
            typeof drawer.open ===
            'function'
          ) {
            drawer.open();
          } else {
            drawer.classList.add('active');

            const innerDrawer =
              drawer.querySelector('.drawer');

            if (innerDrawer) {
              innerDrawer.classList.add(
                'active'
              );
            }
          }
        }
      }

      handleErrorMessage(
        errorMessage = false
      ) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(
            '.product-form__error-message-wrapper'
          );

        if (!this.errorMessageWrapper) {
          return;
        }

        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            '.product-form__error-message'
          );

        this.errorMessageWrapper.toggleAttribute(
          'hidden',
          !errorMessage
        );

        if (
          errorMessage &&
          this.errorMessage
        ) {
          this.errorMessage.textContent =
            errorMessage;
        }
      }
    }
  );
}